import { FC, useState, useCallback } from 'react';
import intl from 'react-intl-universal';
import { SidePanelFilter } from '@getsynapse/design-system';
import { SETTINGS_FILTERS } from 'utils/constants';
import { ProjectsListFilters } from 'types/store/projectsList';
import useProjectsListSidePanelFilters from 'Pages/ProjectsListPage/hooks/useProjectsListSidePanelFilters/useProjectsListSidePanelFilters';
import ProjectsTableFiltersForm from './components/ProjectsTableFiltersForm';

interface ProjectsTableSidePanelFiltersProps {
  tableName: string;
  isConsolidatedView?: boolean;
}

const ProjectsTableSidePanelFilters: FC<ProjectsTableSidePanelFiltersProps> = ({
  tableName,
  isConsolidatedView = false,
}) => {
  const {
    isSidePanelFiltersVisible,
    toggleSidePanelFiltersVisibility,
    handleUpdateFilters,
    clearFilters,
    applyFilters,
    selectedFilters,
    selectedFiltersCount,
  } = useProjectsListSidePanelFilters({
    filterTypeKey: isConsolidatedView
      ? SETTINGS_FILTERS.CONSOLIDATED_PROJECTS_TABLE
      : SETTINGS_FILTERS.ACTIVE_PROJECTS_TABLE,
    isConsolidatedView,
  });
  const [canApplyFilters, setCanApplyFilters] = useState<boolean>(false);

  const onFiltersUpdate = useCallback(
    (filters: ProjectsListFilters) => {
      handleUpdateFilters(filters);
      setCanApplyFilters(true);
    },
    [handleUpdateFilters]
  );

  const onApplyFilters = useCallback(() => {
    applyFilters(selectedFilters);
    setCanApplyFilters(false);
  }, [applyFilters, selectedFilters]);

  const onClearAllFilters = useCallback(() => {
    clearFilters();
    setCanApplyFilters(false);
  }, [clearFilters]);

  return (
    <SidePanelFilter
      open={isSidePanelFiltersVisible}
      onClose={toggleSidePanelFiltersVisibility}
      appliedFiltersCount={selectedFiltersCount}
      canApplyFilters={canApplyFilters}
      onApplyFilters={onApplyFilters}
      onClearAllFilters={onClearAllFilters}
      applyButtonLabel={intl.get('FILTER_PANEL.APPLY')}
      clearAllButtonLabel={intl.get('FILTER_PANEL.CLEAR_ALL')}
      data-testid={`${tableName}__side-panel-filters`}
    >
      <ProjectsTableFiltersForm
        updateFilters={onFiltersUpdate}
        appliedFilters={selectedFilters}
        isConsolidatedView={isConsolidatedView}
      />
    </SidePanelFilter>
  );
};

export default ProjectsTableSidePanelFilters;
